import { IOrderDashboardRow } from '../../../interfaces/garments/merchReport.interface';
import { ThreadStep } from '../../hero/OrderThread';

/*
 * Pure helpers for the Thread dashboard. The order dashboard endpoint gives each order's T&A as
 * aggregates (steps done, steps total, steps overdue), not per-step dates, so a compact thread is
 * drawn from those: done beads, the next open one as "current", and the overdue count marked on the
 * first open beads. The exact per-step dates live on the order's own thread page.
 */

const MAX_BEADS = 12;

/** Local calendar date from a `YYYY-MM-DD` string (no UTC shift). */
export const parseDay = (iso: string): Date => {
    const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
};

export const dayDiff = (fromIso: string, toIso: string): number => {
    const a = parseDay(fromIso);
    const b = parseDay(toIso);
    return Math.round((Date.UTC(b.getFullYear(), b.getMonth(), b.getDate()) - Date.UTC(a.getFullYear(), a.getMonth(), a.getDate())) / 86400000);
};

export const shortDate = (iso?: string | null): string =>
    iso ? parseDay(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '';

export const isLate = (r: IOrderDashboardRow): boolean => r.delayed || r.overdueCount > 0;

/** Late orders first (most overdue steps first), then by ship date, then by order number. */
export function sortThreadRows(rows: IOrderDashboardRow[]): IOrderDashboardRow[] {
    return [...rows].sort((a, b) => {
        const la = isLate(a) ? 1 : 0;
        const lb = isLate(b) ? 1 : 0;
        if (la !== lb) return lb - la;
        if (la && a.overdueCount !== b.overdueCount) return b.overdueCount - a.overdueCount;
        if (a.shipDate !== b.shipDate) return a.shipDate < b.shipDate ? -1 : 1;
        return a.orderNumber.localeCompare(b.orderNumber);
    });
}

/** Beads for one order, from its aggregates. Orders with no T&A calendar get an empty list. */
export function rowToSteps(r: IOrderDashboardRow): ThreadStep[] {
    const total = Math.max(0, Math.min(r.milestoneTotal, MAX_BEADS));
    if (total === 0) return [];
    const done = Math.max(0, Math.min(r.milestoneCompleted, total));
    const open = total - done;
    const lateOpen = Math.max(0, Math.min(r.overdueCount, open));
    return Array.from({ length: total }, (_, i) => {
        const n = i + 1;
        if (i < done) return { key: `s${n}`, label: `Step ${n}`, state: 'done' as const };
        const k = i - done;
        return {
            key: `s${n}`, label: `Step ${n}`,
            state: k === 0 ? ('current' as const) : ('todo' as const),
            late: k < lateOpen,
        };
    });
}

/** "5% · 6 late", "40%", or "Not started". Words, so lateness never rests on colour alone. */
export function progressText(r: IOrderDashboardRow): string {
    if (r.milestoneTotal === 0) return 'No T&A';
    const pct = Math.round(r.progressPct);
    const base = pct === 0 && r.milestoneCompleted === 0 ? 'Not started' : `${pct}%`;
    return r.overdueCount > 0 ? `${base} · ${r.overdueCount} late` : base;
}

/** Orders that ship from `today` through the next 6 days, soonest first. */
export function shippingThisWeek(rows: IOrderDashboardRow[], today: string): IOrderDashboardRow[] {
    return rows
        .filter(r => {
            const d = dayDiff(today, r.shipDate);
            return d >= 0 && d <= 6;
        })
        .sort((a, b) => (a.shipDate === b.shipDate ? a.orderNumber.localeCompare(b.orderNumber) : a.shipDate < b.shipDate ? -1 : 1));
}

export const journeyPath = (orderId: string): string => `/merchandising/orders/${orderId}/journey`;
